import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CLINIC } from "@/lib/brand";
import { Mail, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[color:var(--bg-marketing)]">
      <div className="hidden lg:flex flex-col justify-between p-10 relative overflow-hidden brand-gradient text-white">
        <Link to="/" className="flex items-center gap-3 relative z-10" data-testid="fp-logo-link">
          <img src={CLINIC.logoUrl} alt={CLINIC.name} className="w-12 h-12 rounded-full bg-white p-0.5" />
          <span className="font-heading text-[15px] font-medium leading-tight">{CLINIC.shortName}<br /><span className="text-[10px] tracking-[0.18em] uppercase opacity-80 font-normal">Homoeopathy Clinic</span></span>
        </Link>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-5xl font-light leading-tight">Forgot it?<br /><span className="italic opacity-90">No worries.</span></h1>
          <p className="mt-5 text-white/85 leading-relaxed">We'll send you a secure link to set a new password — it's valid for one hour.</p>
        </div>
        <p className="text-xs text-white/70 relative z-10">© {CLINIC.name}</p>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        {sent ? (
          <div className="w-full max-w-md bg-white border border-[color:var(--border)] rounded-2xl p-8 shadow-sm text-center" data-testid="forgot-sent-card">
            <div className="w-14 h-14 rounded-full bg-[color:var(--primary-light)] grid place-items-center mx-auto">
              <Mail className="w-6 h-6 text-[color:var(--primary-hover)]" strokeWidth={1.6} />
            </div>
            <h2 className="font-heading text-2xl text-[color:var(--ink)] mt-5">Check your inbox</h2>
            <p className="text-sm text-[color:var(--muted)] mt-2 leading-relaxed">
              If <strong className="text-[color:var(--ink)]">{email}</strong> is registered, a reset link is on its way. The link is valid for 1 hour.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1.5 mt-6 text-sm text-[color:var(--secondary)] hover:underline" data-testid="forgot-back-to-login">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="w-full max-w-md bg-white border border-[color:var(--border)] rounded-2xl p-8 shadow-sm"
            data-testid="forgot-password-form"
          >
            <p className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--muted)]">Account recovery</p>
            <h2 className="font-heading text-3xl font-medium text-[color:var(--ink)] mt-1">Reset your password</h2>
            <p className="text-sm text-[color:var(--muted)] mt-2 mb-6">
              Enter the email tied to your account and we'll send you a link to set a new password.
            </p>

            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Email</label>
            <Input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2"
              placeholder="you@example.com"
              data-testid="forgot-email"
            />

            <Button
              type="submit"
              disabled={loading}
              className="w-full mt-6 rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white h-11"
              data-testid="forgot-submit"
            >
              {loading ? "Sending…" : "Send reset link"}
            </Button>

            <Link to="/login" className="block mt-5 text-sm text-[color:var(--muted)] hover:text-[color:var(--ink)] text-center" data-testid="forgot-back-link">
              ← Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
